/**
 * «Список» (§2.3): три фильтра-чипа, сортировка по расстоянию, маршрут по
 * нескольким выбранным великам с финальной точкой — ближайшим хабом.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRideZones, useScoutVehicles, displayNumber, type ScoutVehicle } from '@/api/vehicles'
import { selectActive, useMyTasks } from '@/api/scout'
import { nearestHub, useHubs } from '@/api/hubs'
import { batteryText, describeBike } from '@/lib/describe-bike'
import {
  LIST_FILTERS,
  listFilterLabel,
  listOrder,
  matchesListFilter,
  type ListFilter,
} from '@/lib/list-filter'
import { isOutsideRidingArea } from '@/lib/zones'
import { distanceM, formatDistance, hasPosition, useMyPosition } from '@/lib/geo'
import { multiNavUrl, navUrl, nearestNeighbourOrder } from '@/lib/navigation'
// Алиас, а не голый `toggle`: в экране уже есть свои переключатели, и
// безымянный `toggle` рядом с ними читается как местный.
import {
  clear as clearPicked,
  toggle as togglePickup,
  usePickupSelection,
} from '@/lib/pickup-selection'
import { Chip } from '@/components/ui'

export default function ListPage() {
  const vehicles = useScoutVehicles()
  const zones = useRideZones()
  const mine = useMyTasks()
  const hubs = useHubs()
  const me = useMyPosition()

  const [filter, setFilter] = useState<ListFilter>('all')
  /*
   * Выборка общая с картой и карточкой (`lib/pickup-selection.ts`), а не
   * локальная: раньше она жила в этом состоянии и пропадала при переходе на
   * карточку байка и обратно.
   */
  const selected = usePickupSelection()

  const myClaimedIds = useMemo(
    () => new Set(selectActive(mine.data?.tasks ?? []).map((t) => t.vehicleId)),
    [mine.data],
  )

  const rows = useMemo(() => {
    const all = vehicles.data?.vehicles ?? []
    return all.map((v) => ({
      v,
      status: describeBike(v, zones.data),
      myClaimedTask: myClaimedIds.has(v.id),
      lat: v.lat,
      lng: v.lng,
      // «За зоной» из ТЗ маршрутов — не внутри ни одной разрешённой зоны.
      outside: hasPosition(v) && isOutsideRidingArea(v, zones.data),
    }))
  }, [vehicles.data, zones.data, myClaimedIds])

  const filtered = useMemo(
    () =>
      rows.filter((r) =>
        matchesListFilter({ needsPickup: r.status.needsPickup, myClaimedTask: r.myClaimedTask }, filter),
      ),
    [rows, filter],
  )

  // «За зоной» сверху (Ержан, 01.10), внутри — по расстоянию.
  const sorted = useMemo(() => listOrder(filtered, me, (r) => r.outside), [filtered, me])

  const selectedVehicles = sorted
    .map((r) => r.v)
    .filter((v) => selected.has(v.id) && hasPosition(v))

  function routeUrl(): string | null {
    if (!me || selectedVehicles.length === 0) return null
    const ordered = nearestNeighbourOrder(me, selectedVehicles)
    const last = ordered[ordered.length - 1]!
    const finalHub = nearestHub(last, hubs.data)
    return multiNavUrl(finalHub ? [...ordered, finalHub.hub] : ordered)
  }

  const route = routeUrl()

  return (
    <main className="mx-auto w-full max-w-lg p-4 pb-tabs">
      <h1 className="mb-3 text-xl font-semibold">Список</h1>

      <div className="mb-4 flex gap-2">
        {LIST_FILTERS.map((f) => (
          <button
            key={f}
            aria-pressed={filter === f}
            onClick={() => {
              setFilter(f)
            }}
            className={`min-h-11 flex-1 rounded-full px-3 text-sm font-medium transition ${
              filter === f
                ? 'bg-emerald-500 text-black'
                : 'border border-white/20 text-white/80'
            }`}
          >
            {listFilterLabel[f]}
          </button>
        ))}
      </div>

      {vehicles.isLoading ? (
        <p className="text-sm opacity-60">Загружаем…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm opacity-60">Пусто.</p>
      ) : (
        <ul className="flex flex-col gap-2 pb-20">
          {sorted.map(({ v, status }) => (
            <Row
              key={v.id}
              vehicle={v}
              status={status}
              distance={me && hasPosition(v) ? distanceM(me, v) : null}
              checked={selected.has(v.id)}
              onToggle={() => {
                togglePickup(v.id)
              }}
            />
          ))}
        </ul>
      )}

      {selected.size > 0 ? (
        <div className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-10 p-3">
          {/* Ержан, 01.10: «снять все галочки» одним нажатием. */}
          <button
            onClick={clearPicked}
            className="mb-2 min-h-11 rounded-full bg-black/80 px-4 text-sm underline"
          >
            Снять все
          </button>
          {selectedVehicles.length === 0 ? null : route ? (
            <a
              href={route}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-[56px] items-center justify-center rounded-xl bg-emerald-500
                px-4 text-center font-medium text-black shadow-lg"
            >
              Маршрут по {selectedVehicles.length} великам
            </a>
          ) : (
            <p className="rounded-xl bg-black/80 px-4 py-3 text-center text-sm opacity-70">
              Нужна ваша геопозиция для маршрута
            </p>
          )}
        </div>
      ) : null}
    </main>
  )
}

function Row({
  vehicle,
  status,
  distance,
  checked,
  onToggle,
}: {
  vehicle: ScoutVehicle
  status: ReturnType<typeof describeBike>
  distance: number | null
  checked: boolean
  onToggle: () => void
}) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
      <input
        type="checkbox"
        aria-label={`выбрать ${displayNumber(vehicle) || 'байк'}`}
        checked={checked}
        onChange={onToggle}
        className="size-6 shrink-0"
      />
      <Link to={`/bike/${vehicle.id}`} className="min-w-0 flex-1">
        <div className="font-mono text-lg font-bold">{displayNumber(vehicle) || 'Без номера'}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {status.reasons.length > 0 ? (
            status.reasons.map((r) => (
              <Chip key={r.code} tone={status.tone}>
                {r.text}
              </Chip>
            ))
          ) : (
            <Chip tone={status.tone}>{status.label}</Chip>
          )}
        </div>
        {/*
          Заряд — отдельным полем (§2.3), а не только внутри чипа «Разряжен».
          У чипа «Разряжен» число и так есть, но у исправного байка без
          чипа-причины заряда не видно вовсе без этой строки.
        */}
        <div className="mt-1 text-sm opacity-60">
          {batteryText(vehicle)}
          {' · '}
          {distance !== null ? formatDistance(distance) : 'расстояние неизвестно'}
        </div>
      </Link>
      {hasPosition(vehicle) ? (
        <a
          href={navUrl(vehicle)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            e.stopPropagation()
          }}
          className="min-h-11 shrink-0 rounded-lg border border-white/20 px-3 py-2 text-sm
            font-medium"
        >
          Маршрут
        </a>
      ) : null}
    </li>
  )
}
